import { ReactNode, RefObject, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { workTypeDesignTokens } from "../work-type-design-tokens";

export interface AsyncEntity {
  id: string;
  name: string;
  description: string;
}

interface AsyncEntityPickerProps {
  label: string;
  variant: "department" | "employee";
  searchEntities: (query: string) => Promise<AsyncEntity[]>;
  selected: AsyncEntity[];
  onSelectionChange: (selected: AsyncEntity[]) => void;
  debounceMs?: number;
  initialQuery?: string;
  disabled?: boolean;
  presentation?: "inline" | "dialog";
  previewLimit?: number;
  searchPlaceholder?: string;
  dialogTitle?: string;
  dialogDescription?: string;
  ariaDescribedBy?: string;
}

type SearchState =
  | "idle"
  | "debouncing"
  | "loading"
  | "results"
  | "empty"
  | "error";
type DialogMode = "search" | "selected" | null;

const Picker = styled.section`
  display: grid;
  gap: 8px;
  min-width: 0;
`;

const Label = styled.label`
  display: grid;
  gap: 5px;
  font-weight: 700;
`;

const SearchInput = styled.input<{ $modern?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  min-height: ${props =>
    props.$modern ? workTypeDesignTokens.control.height : "auto"};
  border: ${props =>
    props.$modern
      ? `1px solid ${workTypeDesignTokens.color.borderStrong}`
      : undefined};
  border-radius: ${props =>
    props.$modern ? workTypeDesignTokens.radius.control : undefined};
  padding: ${props => (props.$modern ? "0 12px" : undefined)};

  &:focus-visible {
    outline: ${props =>
      props.$modern ? workTypeDesignTokens.focus.outline : undefined};
    outline-offset: ${props =>
      props.$modern ? workTypeDesignTokens.focus.offset : undefined};
  }
`;

const ResultList = styled.ul<{ $dialog?: boolean }>`
  display: grid;
  gap: 4px;
  max-height: ${props => (props.$dialog ? "none" : "180px")};
  overflow-y: ${props => (props.$dialog ? "visible" : "auto")};
  border: 1px solid ${props =>
    props.$dialog
      ? workTypeDesignTokens.color.border
      : props.theme.colors.frameGrey};
  border-radius: ${props =>
    props.$dialog ? workTypeDesignTokens.radius.control : "4px"};
  padding: 6px;
  margin: 0;
  list-style: none;
`;

const Result = styled.li`
  border-radius: ${workTypeDesignTokens.radius.control};

  > label {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 10px;
    align-items: start;
    min-height: 44px;
    padding: 9px 10px;
    box-sizing: border-box;
    cursor: pointer;
  }

  &:hover {
    background: ${workTypeDesignTokens.color.surfaceMuted};
  }
`;

const InlineResult = styled.label`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  padding: 6px;
  border-radius: 4px;

  &:hover {
    background: ${props => props.theme.colors.lightGrey};
  }
`;

const InlineResultList = styled.div`
  display: grid;
  gap: 4px;
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid ${props => props.theme.colors.frameGrey};
  border-radius: 4px;
  padding: 6px;
`;

const Description = styled.span<{ $modern?: boolean }>`
  display: block;
  margin-top: 2px;
  color: ${props =>
    props.$modern
      ? workTypeDesignTokens.color.textMuted
      : props.theme.colors.darkGrey};
  overflow-wrap: anywhere;
  line-height: ${props => (props.$modern ? "1.4" : "normal")};
`;

const Chips = styled.ul<{ $modern?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0;
  margin: 0;
  list-style: none;
`;

const Chip = styled.li<{ $modern?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  min-height: ${props => (props.$modern ? "30px" : "auto")};
  padding: ${props => (props.$modern ? "4px 10px" : "4px 7px")};
  border: 1px solid
    ${props =>
      props.$modern
        ? workTypeDesignTokens.tone.selected.border
        : props.theme.colors.green};
  border-radius: 999px;
  color: ${props =>
    props.$modern
      ? workTypeDesignTokens.tone.selected.text
      : "inherit"};
  background: ${props =>
    props.$modern
      ? workTypeDesignTokens.tone.selected.background
      : props.theme.colors.softGreen};

  button {
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
  }
`;

const PickerHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button<{ $primary?: boolean; $danger?: boolean }>`
  min-height: 40px;
  border: 1px solid
    ${props =>
      props.$danger
        ? workTypeDesignTokens.tone.danger.border
        : props.$primary
        ? workTypeDesignTokens.color.focus
        : workTypeDesignTokens.color.borderStrong};
  border-radius: ${workTypeDesignTokens.radius.control};
  padding: 0 14px;
  background: ${props =>
    props.$danger
      ? workTypeDesignTokens.tone.danger.background
      : props.$primary
      ? workTypeDesignTokens.color.focus
      : workTypeDesignTokens.color.surface};
  color: ${props =>
    props.$danger
      ? workTypeDesignTokens.tone.danger.text
      : props.$primary
      ? "#FFFFFF"
      : workTypeDesignTokens.color.text};
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: ${workTypeDesignTokens.focus.outline};
    outline-offset: ${workTypeDesignTokens.focus.offset};
  }
`;

const OverflowCount = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 10px;
  border: 1px dashed ${workTypeDesignTokens.tone.selected.border};
  border-radius: ${workTypeDesignTokens.radius.pill};
  color: ${workTypeDesignTokens.color.textMuted};
  font-weight: 700;
`;

const DialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 24px;
  box-sizing: border-box;
  background: rgba(15, 23, 42, 0.48);
`;

const DialogPanel = styled.div`
  display: flex;
  flex-direction: column;
  width: min(720px, calc(100vw - 48px));
  max-height: calc(100vh - 48px);
  overflow: hidden;
  border: 1px solid ${workTypeDesignTokens.color.border};
  border-radius: ${workTypeDesignTokens.radius.dialog};
  background: ${workTypeDesignTokens.color.surface};
  color: ${workTypeDesignTokens.color.text};
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.24);
`;

const DialogHeader = styled.header`
  display: grid;
  gap: 4px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid ${workTypeDesignTokens.color.border};

  h2,
  p {
    margin: 0;
  }

  p {
    color: ${workTypeDesignTokens.color.textMuted};
  }
`;

const DialogBody = styled.div`
  display: grid;
  gap: 12px;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 24px;
`;

const DialogFooter = styled.footer`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px;
  border-top: 1px solid ${workTypeDesignTokens.color.border};
  background: ${workTypeDesignTokens.color.surface};
`;

const SelectedList = styled.ul`
  display: grid;
  gap: 6px;
  padding: 0;
  margin: 0;
  list-style: none;
`;

const SelectedRow = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid ${workTypeDesignTokens.color.border};
  border-radius: ${workTypeDesignTokens.radius.control};
`;

function uniqueEntities(entities: AsyncEntity[]) {
  const seen = new Set<string>();
  return entities.filter(entity => {
    if (seen.has(entity.id)) return false;
    seen.add(entity.id);
    return true;
  });
}

function previewLabel(entity: AsyncEntity, variant: "department" | "employee") {
  if (variant === "department") return entity.name;
  const employeeNumber = entity.description.split("·")[0].trim();
  return employeeNumber ? `${entity.name} · ${employeeNumber}` : entity.name;
}

function withObjectParticle(label: string) {
  const lastCode = label.charCodeAt(label.length - 1);
  const hangulOffset = lastCode - 0xac00;
  const hasFinalConsonant =
    hangulOffset >= 0 && hangulOffset <= 11171 && hangulOffset % 28 > 0;
  return `${label}${hasFinalConsonant ? "을" : "를"}`;
}

function DialogSurface({
  title,
  description,
  initialFocusRef,
  returnFocusTarget,
  onClose,
  children,
  footer
}: {
  title: string;
  description: string;
  initialFocusRef: RefObject<HTMLElement>;
  returnFocusTarget: RefObject<HTMLElement>;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    returnFocusRef.current =
      returnFocusTarget.current || (document.activeElement as HTMLElement | null);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    initialFocusRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      returnFocusRef.current?.focus();
    };
  }, [initialFocusRef, returnFocusTarget]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== "Tab" || !dialogRef.current) return;

    const focusable = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(
        "button:not([disabled]), input:not([disabled]), [href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
      )
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <DialogOverlay>
      <DialogPanel
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <h2>{title}</h2>
          <p>{description}</p>
        </DialogHeader>
        <DialogBody>{children}</DialogBody>
        <DialogFooter>{footer}</DialogFooter>
      </DialogPanel>
    </DialogOverlay>
  );
}

export const AsyncEntityPicker = ({
  label,
  variant,
  searchEntities,
  selected,
  onSelectionChange,
  debounceMs = 300,
  initialQuery = "",
  disabled = false,
  presentation = "inline",
  previewLimit = 5,
  searchPlaceholder: searchPlaceholderOverride,
  dialogTitle,
  dialogDescription,
  ariaDescribedBy
}: AsyncEntityPickerProps) => {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<AsyncEntity[]>([]);
  const [searchState, setSearchState] = useState<SearchState>(
    initialQuery ? "debouncing" : "idle"
  );
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [temporarySelected, setTemporarySelected] = useState<AsyncEntity[]>([]);
  const [selectedQuery, setSelectedQuery] = useState("");
  const requestSequence = useRef(0);
  const dialogSearchRef = useRef<HTMLInputElement>(null);
  const selectedSearchRef = useRef<HTMLInputElement>(null);
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  const selectedTriggerRef = useRef<HTMLButtonElement>(null);

  const searchIsActive = presentation === "inline" || dialogMode === "search";
  useEffect(() => {
    if (!searchIsActive || !query.trim()) {
      requestSequence.current += 1;
      setResults([]);
      setSearchState("idle");
      return;
    }

    setSearchState("debouncing");
    const requestId = ++requestSequence.current;
    const timer = window.setTimeout(async () => {
      setSearchState("loading");
      try {
        const nextResults = uniqueEntities(await searchEntities(query.trim())).slice(
          0,
          30
        );
        if (requestSequence.current !== requestId) return;
        setResults(nextResults);
        setSearchState(nextResults.length === 0 ? "empty" : "results");
      } catch {
        if (requestSequence.current !== requestId) return;
        setResults([]);
        setSearchState("error");
      }
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [debounceMs, query, searchEntities, searchIsActive]);

  const searchStatus =
    searchState === "debouncing"
      ? "검색어 입력을 기다리는 중"
      : searchState === "loading"
      ? "검색 중"
      : searchState === "empty"
      ? "검색 결과가 없습니다."
      : searchState === "error"
      ? "검색에 실패했습니다. 다시 시도하세요."
      : searchState === "results"
      ? `${results.length}개 결과`
      : "";
  const searchPlaceholder =
    searchPlaceholderOverride ??
    (variant === "department" ? "부서 이름 또는 조직 경로" : "이름, 사번 또는 부서");
  const selectedUnit = variant === "employee" ? "명" : "개";
  const temporaryIds = new Set(temporarySelected.map(entity => entity.id));
  const selectedIds = new Set(selected.map(entity => entity.id));

  const openSearch = () => {
    setTemporarySelected(uniqueEntities(selected));
    setQuery(initialQuery);
    setDialogMode("search");
  };
  const openSelected = () => {
    setTemporarySelected(uniqueEntities(selected));
    setSelectedQuery("");
    setDialogMode("selected");
  };
  const closeDialog = () => {
    setTemporarySelected([]);
    setDialogMode(null);
  };
  const commitTemporary = () => {
    onSelectionChange(uniqueEntities(temporarySelected));
    setDialogMode(null);
  };
  const toggleTemporary = (entity: AsyncEntity, checked: boolean) => {
    setTemporarySelected(current =>
      checked
        ? uniqueEntities([...current, entity])
        : current.filter(candidate => candidate.id !== entity.id)
    );
  };

  if (presentation === "inline") {
    const selectedIds = new Set(selected.map(entity => entity.id));
    return (
      <Picker aria-label={`${label} 선택기`} aria-describedby={ariaDescribedBy}>
        <Label>
          {label}
          <input
            type="search"
            value={query}
            disabled={disabled}
            aria-label={`${label} 검색`}
            placeholder={searchPlaceholder}
            onChange={event => setQuery(event.currentTarget.value)}
          />
        </Label>
        <div aria-live="polite" role="status">
          {searchStatus}
        </div>
        {results.length > 0 && (
          <InlineResultList role="group" aria-label={`${label} 검색 결과`}>
            {results.map(entity => (
              <InlineResult key={entity.id}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(entity.id)}
                  disabled={disabled}
                  aria-label={`${entity.name} ${entity.description}`}
                  onChange={event => {
                    if (event.currentTarget.checked && !selectedIds.has(entity.id)) {
                      onSelectionChange([...selected, entity]);
                    } else if (!event.currentTarget.checked) {
                      onSelectionChange(
                        selected.filter(current => current.id !== entity.id)
                      );
                    }
                  }}
                />
                <span>
                  <strong>{entity.name}</strong>
                  <Description>{entity.description}</Description>
                </span>
              </InlineResult>
            ))}
          </InlineResultList>
        )}
        <div>
          <strong>선택됨 {selected.length}개</strong>
          <Chips aria-label={`${label} 선택 목록`}>
            {selected.map(entity => (
              <Chip key={entity.id}>
                <span>{entity.name}</span>
                {!disabled && (
                  <button
                    type="button"
                    aria-label={`${entity.name} 선택 제거`}
                    onClick={() =>
                      onSelectionChange(
                        selected.filter(current => current.id !== entity.id)
                      )
                    }
                  >
                    ×
                  </button>
                )}
              </Chip>
            ))}
          </Chips>
        </div>
      </Picker>
    );
  }

  const preview = selected.slice(0, Math.max(0, previewLimit));
  const overflowCount = Math.max(0, selected.length - preview.length);
  const filteredSelected = temporarySelected.filter(entity =>
    `${entity.name} ${entity.description}`
      .toLocaleLowerCase()
      .includes(selectedQuery.trim().toLocaleLowerCase())
  );

  return (
    <Picker aria-label={`${label} 선택기`} aria-describedby={ariaDescribedBy}>
      <PickerHeader>
        <div>
          <strong>{label}</strong>
          <div aria-live="polite">선택됨 {selected.length}{selectedUnit}</div>
        </div>
        <Actions>
          <ActionButton
            ref={searchTriggerRef}
            type="button"
            disabled={disabled}
            onClick={openSearch}
          >
            {label} 검색 열기
          </ActionButton>
          <ActionButton
            ref={selectedTriggerRef}
            type="button"
            disabled={disabled || selected.length === 0}
            onClick={openSelected}
          >
            전체 목록 보기
          </ActionButton>
          {selected.length > 0 && !disabled && (
            <ActionButton
              type="button"
              $danger
              onClick={() => onSelectionChange([])}
            >
              전체 해제
            </ActionButton>
          )}
        </Actions>
      </PickerHeader>
      <Chips $modern aria-label={`${label} 선택 미리보기`}>
        {preview.map(entity => (
          <Chip $modern key={entity.id}>
            <span>{previewLabel(entity, variant)}</span>
            {!disabled && (
              <button
                type="button"
                aria-label={`${previewLabel(entity, variant)} 선택 제거`}
                onClick={() =>
                  onSelectionChange(
                    selected.filter(current => current.id !== entity.id)
                  )
                }
              >
                ×
              </button>
            )}
          </Chip>
        ))}
      </Chips>
      {overflowCount > 0 && (
        <OverflowCount>+{overflowCount}{selectedUnit} 더보기</OverflowCount>
      )}

      {dialogMode === "search" && (
        <DialogSurface
          title={dialogTitle ?? `${label} 검색`}
          description={
            dialogDescription ??
            `${searchPlaceholder}로 검색하고 선택 추가에서 반영합니다.`
          }
          initialFocusRef={dialogSearchRef}
          returnFocusTarget={searchTriggerRef}
          onClose={closeDialog}
          footer={
            <>
              <strong aria-live="polite">
                임시 선택 {temporarySelected.length}{selectedUnit}
              </strong>
              <ActionButton type="button" onClick={closeDialog}>
                취소
              </ActionButton>
              <ActionButton type="button" $primary onClick={commitTemporary}>
                선택 추가
              </ActionButton>
            </>
          }
        >
          <Label>
            {label} 검색
            <SearchInput
              ref={dialogSearchRef}
              $modern
              type="search"
              value={query}
              aria-label={`${label} 검색`}
              placeholder={searchPlaceholder}
              onChange={event => setQuery(event.currentTarget.value)}
            />
          </Label>
          <div aria-live="polite" role="status">
            {searchStatus}
          </div>
          {results.length > 0 && (
            <ResultList $dialog aria-label={`${label} 검색 결과`}>
              {results.map(entity => (
                <Result key={entity.id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={temporaryIds.has(entity.id)}
                      disabled={selectedIds.has(entity.id)}
                      aria-label={`${entity.name} ${entity.description}`}
                      onChange={event =>
                        toggleTemporary(entity, event.currentTarget.checked)
                      }
                    />
                    <span>
                      <strong>{entity.name}</strong>
                      <Description $modern>{entity.description}</Description>
                      {selectedIds.has(entity.id) && (
                        <Description $modern>추가됨</Description>
                      )}
                    </span>
                  </label>
                </Result>
              ))}
            </ResultList>
          )}
        </DialogSurface>
      )}

      {dialogMode === "selected" && (
        <DialogSurface
          title={`${label} 전체 목록`}
          description={`선택된 ${withObjectParticle(
            label
          )} 검색하거나 제외한 뒤 확인에서 반영합니다.`}
          initialFocusRef={selectedSearchRef}
          returnFocusTarget={selectedTriggerRef}
          onClose={closeDialog}
          footer={
            <>
              <ActionButton type="button" onClick={closeDialog}>
                취소
              </ActionButton>
              <ActionButton type="button" $primary onClick={commitTemporary}>
                확인
              </ActionButton>
            </>
          }
        >
          <Label>
            선택된 {label} 검색
            <SearchInput
              ref={selectedSearchRef}
              $modern
              type="search"
              value={selectedQuery}
              aria-label={`선택된 ${label} 검색`}
              onChange={event => setSelectedQuery(event.currentTarget.value)}
            />
          </Label>
          <Actions>
            <strong>임시 선택 {temporarySelected.length}{selectedUnit}</strong>
            <ActionButton
              type="button"
              $danger
              disabled={temporarySelected.length === 0}
              onClick={() => setTemporarySelected([])}
            >
              전체 해제
            </ActionButton>
          </Actions>
          <SelectedList aria-label={`${label} 전체 선택 목록`}>
            {filteredSelected.map(entity => (
              <SelectedRow key={entity.id}>
                <span>
                  <strong>{entity.name}</strong>
                  <Description $modern>{entity.description}</Description>
                </span>
                <ActionButton
                  type="button"
                  aria-label={`${previewLabel(entity, variant)} 선택 제외`}
                  onClick={() =>
                    setTemporarySelected(current =>
                      current.filter(candidate => candidate.id !== entity.id)
                    )
                  }
                >
                  제외
                </ActionButton>
              </SelectedRow>
            ))}
          </SelectedList>
        </DialogSurface>
      )}
    </Picker>
  );
};
